import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import api from "../../api/sellerApi";
import StarRating from "../../components/StarRating";

const STATUS_STEPS = [
  { key: "PENDING", label: "Order confirmed" },
  { key: "ASSIGNED", label: "Rider assigned" },
  { key: "PICKED_UP", label: "Picked up" },
  { key: "IN_TRANSIT", label: "In transit" },
  { key: "DELIVERED", label: "Delivered" },
];

function useElapsed(startIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startIso) return;
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [startIso]);
  return Math.max(0, Math.floor((now - new Date(startIso).getTime()) / 1000));
}

function humanElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

export default function OrderTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const [riderRating, setRiderRating] = useState(0);
  const [riderComment, setRiderComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingStatus, setRatingStatus] = useState(null); // null | 'submitted' | 'already'
  const [etaData, setEtaData] = useState(null);

  // Poll ETA endpoint every 30s while active
  useEffect(() => {
    if (
      !order ||
      !["ASSIGNED", "PICKED_UP", "IN_TRANSIT"].includes(order.status)
    ) {
      setEtaData(null);
      return;
    }

    const fetchEta = () => {
      api
        .get(`/orders/${id}/eta`)
        .then((res) => setEtaData(res.data))
        .catch(() => setEtaData(null));
    };

    fetchEta();
    const interval = setInterval(fetchEta, 30000);
    return () => clearInterval(interval);
  }, [id, order?.status]);

  const fetchOrder = () => {
    api
      .get(`/orders/${id}`)
      .then((res) => setOrder(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    api
      .get(`/orders/${id}`)
      .then((res) => setOrder(res.data))
      .catch(() => navigate("/seller"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Check if already rated when delivered
  useEffect(() => {
    if (order?.status === "DELIVERED") {
      api
        .get(`/orders/${id}/rating`)
        .then((res) => {
          if (res.data.rated) {
            setRatingStatus("already");
            setRiderRating(res.data.rating);
          }
        })
        .catch(() => {});
    }
  }, [id, order?.status]);

  const submitRiderRating = async () => {
    if (!riderRating) return;
    setRatingSubmitting(true);
    try {
      await api.post(`/orders/${id}/rate-rider`, {
        rating: riderRating,
        comment: riderComment.trim() || null,
      });
      setRatingStatus("submitted");
    } catch (err) {
      if (err.response?.data?.error?.includes("already")) {
        setRatingStatus("already");
      } else {
        alert(err.response?.data?.error || "Failed to submit rating.");
      }
    } finally {
      setRatingSubmitting(false);
    }
  };

  // Socket.io for real-time updates
  useEffect(() => {
    const token = localStorage.getItem("byka_token");
    if (!token || !order) return;

    const socketUrl =
      process.env.NODE_ENV === "production"
        ? undefined
        : "http://localhost:3001";
    const socket = io(socketUrl, {
      auth: { token, role: "seller" },
      transports: ["websocket", "polling"],
    });

    socket.on("order:status_update", (data) => {
      if (data.order_id === id) fetchOrder();
    });

    socket.on("order:rider_assigned", (data) => {
      if (data.order_id === id) fetchOrder();
    });

    socket.on("order:no_riders", (data) => {
      if (data.order_id === id) {
        setOrder((prev) => (prev ? { ...prev, _noRiders: true } : prev));
      }
    });

    return () => socket.disconnect();
  }, [id, order?.id]);

  const cancelOrder = async () => {
    if (!window.confirm("Are you sure you want to cancel this delivery?"))
      return;
    try {
      await api.post(`/orders/${id}/cancel`);
      navigate("/seller");
    } catch (err) {
      alert(err.response?.data?.error || "Cannot cancel this order.");
    }
  };

  const resendOtp = async () => {
    setResending(true);
    try {
      const res = await api.post(`/orders/${id}/resend-otp`);
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to resend code.");
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!order) return null;

  const currentStepIndex = STATUS_STEPS.findIndex(
    (s) => s.key === order.status,
  );
  const isCancelled = order.status === "CANCELLED";
  const isDelivered = order.status === "DELIVERED";
  const canCancel = ["PENDING", "ASSIGNED"].includes(order.status);
  const elapsedSec = useElapsed(order.created_at);

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-6">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/seller", { replace: true })}
          aria-label="Back to home"
          className="text-gray-500 hover:text-gray-700 p-1 -ml-1"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900 flex-1">
          Order #{order.order_number}
        </h1>
        <div className="flex gap-2">
          {order.express && (
            <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-medium">
              Express
            </span>
          )}
          {order.otp_verified && (
            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
              {order.otp_bypassed ? "Bypassed" : "Verified"}
            </span>
          )}
        </div>
      </div>

      {isCancelled ? (
        <div className="bg-red-50 rounded-2xl p-5 text-center">
          <p className="text-red-700 font-bold text-lg">Order Cancelled</p>
          {order.cancellation_reason && (
            <p className="text-red-600 text-sm mt-1">
              {order.cancellation_reason}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Status Progress */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4">
            <div className="space-y-4">
              {STATUS_STEPS.map((step, i) => (
                <div key={step.key} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        i <= currentStepIndex ? "bg-green-500" : "bg-gray-200"
                      }`}
                    >
                      {i <= currentStepIndex ? (
                        <svg
                          className="w-3.5 h-3.5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <div className="w-2 h-2 bg-gray-400 rounded-full" />
                      )}
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div
                        className={`w-0.5 h-6 ${i < currentStepIndex ? "bg-green-500" : "bg-gray-200"}`}
                      />
                    )}
                  </div>
                  <div>
                    <p
                      className={`text-sm font-medium ${i <= currentStepIndex ? "text-gray-900" : "text-gray-400"}`}
                    >
                      {step.label}
                    </p>
                    {order.status === "PENDING" && i === 0 && (
                      <div className="mt-1 space-y-1">
                        <p className="text-xs text-gray-500">
                          Looking for a rider — {humanElapsed(elapsedSec)}
                        </p>
                        {elapsedSec > 180 && (
                          <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
                            This is taking longer than usual. All riders may be
                            on active deliveries.
                          </p>
                        )}
                        {order._noRiders && (
                          <p className="text-xs text-yellow-600 mt-0.5">
                            All riders are busy. Your order is queued.
                          </p>
                        )}
                      </div>
                    )}
                    {["ASSIGNED", "PICKED_UP", "IN_TRANSIT"].includes(
                      order.status,
                    ) &&
                      i === currentStepIndex && (
                        <div className="mt-1">
                          {etaData ? (
                            <p className="text-xs text-emerald-700 font-medium bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200 inline-block">
                              {etaData.confidence === "low" ? "Estimated " : ""}
                              {etaData.leg === "to_pickup"
                                ? `Rider arriving at pickup in ~${etaData.eta_min_low}–${etaData.eta_min_high} min`
                                : `Delivery arriving in ~${etaData.eta_min_low}–${etaData.eta_min_high} min`}
                            </p>
                          ) : (
                            order.status === "ASSIGNED" && (
                              <p className="text-xs text-gray-500">
                                Pickup usually in 8–15 minutes.
                              </p>
                            )
                          )}
                        </div>
                      )}
                    {i === 4 && isDelivered && order.otp_verified && (
                      <p className="text-xs text-green-600 mt-0.5">
                        Confirmed by buyer with code{" "}
                        {order.otp_bypassed ? "(admin override)" : ""}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* OTP Card - show when order has OTP and not yet delivered */}
          {order.delivery_otp && !isDelivered && (
            <div className="bg-green-50 rounded-2xl border border-green-200 p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-green-800 uppercase font-semibold">
                  Buyer's confirmation code
                </p>
                <button
                  onClick={resendOtp}
                  disabled={resending}
                  className="text-xs bg-green-600 text-white px-3 py-1 rounded-lg font-medium disabled:opacity-50"
                >
                  {resending ? "Sending..." : "Resend to buyer"}
                </button>
              </div>
              <p className="text-3xl font-bold text-green-800 tracking-widest text-center">
                {order.delivery_otp}
              </p>
              <div className="bg-green-100 rounded-xl p-3 mt-3">
                <p className="text-xs text-green-800 font-semibold mb-1">
                  Share this code with your buyer
                </p>
                <p className="text-xs text-green-700">
                  Please WhatsApp this 4-digit code to your buyer (
                  {order.buyer_name}). The rider will ask for it on delivery to
                  confirm receipt.
                </p>
              </div>
            </div>
          )}

          {/* Delivered: OTP verified + photo badge */}
          {isDelivered && order.delivery_otp && (
            <div className="bg-green-50 rounded-2xl border border-green-200 p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                <p className="font-semibold text-green-800">
                  {order.otp_bypassed
                    ? "Delivery confirmed (admin override)"
                    : "Delivery confirmed by buyer"}
                </p>
              </div>
              {order.otp_bypassed && order.otp_bypass_reason && (
                <p className="text-xs text-green-600 ml-7">
                  Reason: {order.otp_bypass_reason}
                </p>
              )}
            </div>
          )}

          {/* Rider Info */}
          {order.rider_name && (
            <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
                Your rider
              </p>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-gray-900">{order.rider_name}</p>
                  {order.rider_avg_rating && (
                    <div className="flex items-center gap-1 mt-1">
                      <StarRating
                        value={Math.round(parseFloat(order.rider_avg_rating))}
                        readOnly
                        size={14}
                      />
                      <span className="text-xs text-gray-500">
                        {parseFloat(order.rider_avg_rating).toFixed(1)} (
                        {order.rider_total_ratings})
                      </span>
                    </div>
                  )}
                </div>
                <a
                  href={`tel:${order.rider_phone}`}
                  className="bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  Call
                </a>
              </div>
            </div>
          )}

          {/* Delivery Photos (shown after delivery) */}
          {isDelivered &&
            order.delivery_photos &&
            order.delivery_photos.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
                <p className="text-xs text-gray-500 uppercase font-semibold mb-3">
                  Delivery proof photo
                </p>
                {order.delivery_photos.map((photo) => (
                  <div key={photo.id}>
                    <img
                      src={photo.photo_url}
                      alt="Delivery proof"
                      className={`w-full rounded-xl border border-gray-200 cursor-pointer ${photoExpanded ? "" : "max-h-48 object-cover"}`}
                      onClick={() => setPhotoExpanded(!photoExpanded)}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-center">
                      {new Date(photo.taken_at).toLocaleString()}{" "}
                      {photoExpanded ? "(tap to collapse)" : "(tap to enlarge)"}
                    </p>
                  </div>
                ))}
              </div>
            )}

          {/* Rate Rider (shown after delivery) */}
          {isDelivered && order.rider_name && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4">
              {ratingStatus === "submitted" ? (
                <div className="text-center">
                  <div className="flex justify-center mb-2">
                    <StarRating value={riderRating} readOnly size={28} />
                  </div>
                  <p className="text-green-600 font-semibold">
                    Thank you for your rating!
                  </p>
                </div>
              ) : ratingStatus === "already" ? (
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
                    Your rating for {order.rider_name}
                  </p>
                  <div className="flex justify-center">
                    <StarRating value={riderRating} readOnly size={28} />
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    How was your rider?
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Rate {order.rider_name}'s service
                  </p>
                  <div className="flex justify-center mb-3">
                    <StarRating
                      value={riderRating}
                      onChange={setRiderRating}
                      size={36}
                    />
                  </div>
                  {riderRating > 0 && (
                    <>
                      <textarea
                        value={riderComment}
                        onChange={(e) => setRiderComment(e.target.value)}
                        placeholder="Optional comment..."
                        rows={2}
                        className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-700 mb-3 resize-none"
                      />
                      <button
                        onClick={submitRiderRating}
                        disabled={ratingSubmitting}
                        className="w-full py-3 rounded-xl bg-green-600 text-white font-bold text-sm disabled:opacity-50"
                      >
                        {ratingSubmitting ? "Submitting..." : "Submit Rating"}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Order Details */}
      <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 mb-4">
        <div className="p-4">
          <p className="text-xs text-gray-500">Buyer</p>
          <p className="text-sm font-medium text-gray-900">
            {order.buyer_name}
          </p>
          <p className="text-sm text-gray-600">{order.buyer_phone}</p>
        </div>
        <div className="p-4">
          <p className="text-xs text-gray-500">Drop-off</p>
          <p className="text-sm text-gray-900">{order.dropoff_address}</p>
        </div>
        <div className="p-4 flex justify-between">
          <span className="text-sm text-gray-500">Delivery fee</span>
          <span className="text-sm font-bold text-gray-900">
            ${parseFloat(order.final_fee).toFixed(2)}
          </span>
        </div>
        {order.cod_enabled && (
          <div className="p-4 flex justify-between">
            <span className="text-sm text-gray-500">COD to collect</span>
            <span className="text-sm font-bold text-yellow-700">
              ${parseFloat(order.cod_amount).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {canCancel && (
        <div className="mt-2">
          <button
            onClick={cancelOrder}
            className="w-full border-2 border-red-200 text-red-600 py-3 rounded-xl font-medium text-sm hover:bg-red-50"
          >
            Cancel Order
          </button>
          {order.status === "PENDING" && (
            <p className="text-xs text-gray-400 mt-1.5 text-center">
              You won't be charged for cancelled orders.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
